<template>
  <div class="hms-page">
    <Navigation />

    <div class="container-fluid hms-page-body">
      <!-- HEADER -->
      <div class="d-flex align-items-center justify-content-between mb-3 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <div>
            <h4 class="hms-page-title m-0"><i class="mdi mdi-chart-timeline-variant me-2"></i>Visits Summary</h4>
            <div class="hms-muted-sm">Find visit summary here</div>
          </div>
        </div>
        <div class="d-flex gap-2 align-items-center flex-wrap">
          <select class="hms-input hms-filter" v-model="branchFilter" @change="loadAll">
            <option value="">Filter by facility</option>
            <option v-for="b in branches" :key="b.branch_id" :value="b.branch_id">{{ b.branch_name }}</option>
          </select>
          <select class="hms-input hms-filter" v-model="visitStatusFilter" @change="loadAll">
            <option value="">Filter by visit status</option>
            <option value="waiting">Waiting</option>
            <option value="in_progress">In progress</option>
            <option value="completed">Completed / Finished</option>
            <option value="cancelled">Cancelled</option>
            <option value="no_show">No show</option>
          </select>
          <select class="hms-input hms-filter" v-model="paymentStatusFilter" @change="loadAll">
            <option value="">Filter by billing class</option>
            <option value="paid">Paid</option>
            <option value="partial">Partial</option>
            <option value="unpaid">Unpaid</option>
            <option value="insurance_pending">Insurance pending</option>
            <option value="waived">Waived</option>
          </select>
          <button class="hms-btn hms-btn-blue" @click="loadAll" :disabled="loading">
            <i class="mdi mdi-refresh" :class="{ 'mdi-spin': loading }"></i>
          </button>
          <button class="hms-btn hms-btn-blue" @click="printPage"><i class="mdi mdi-printer me-1"></i>Print</button>
        </div>
      </div>

      <div v-if="loading && !data.Today" class="hms-spinner"><div class="spinner-border"></div></div>

      <div v-else class="hms-print-area">
        <!-- ROW 1: Period cards + Status bar chart -->
        <div class="row g-3 mb-3">
          <div class="col-lg-2 col-md-4" v-for="c in periodCards" :key="c.key">
            <div class="hms-vs-card">
              <div class="hms-vs-lbl">{{ c.label }}</div>
              <div class="hms-vs-sub">Visits</div>
              <div class="hms-vs-num"><i class="mdi mdi-eye-outline me-1"></i>{{ formatNum(c.visits) }}</div>
              <div class="hms-vs-delta" :class="deltaClass(c.visits_delta)">
                <i class="mdi" :class="deltaIcon(c.visits_delta)"></i>
                {{ formatDelta(c.visits_delta) }} from {{ c.prev_label }}
              </div>
              <hr class="hms-vs-sep">
              <div class="hms-vs-sub">Invoiced</div>
              <div class="hms-vs-num-sm">KES {{ formatNum(c.invoiced) }}</div>
              <div class="hms-vs-delta" :class="deltaClass(c.invoiced_delta)">
                <i class="mdi" :class="deltaIcon(c.invoiced_delta)"></i>
                KES {{ formatDelta(c.invoiced_delta) }} from {{ c.prev_label }}
              </div>
            </div>
          </div>

          <!-- Visit Breakdown by Status -->
          <div class="col-lg-6 col-md-12">
            <div class="hms-card h-100">
              <div class="hms-card-head">
                Visit Breakdown by Status
                <span class="hms-muted-sm ms-2">{{ data.MonthStart }} → {{ data.MonthEnd }}</span>
              </div>
              <div class="hms-card-body">
                <div v-if="!byStatusRows.length" class="hms-muted text-center hms-pad">No data for the period.</div>
                <div v-else class="hms-vs-status-list">
                  <div v-for="r in byStatusRows" :key="r.status" class="hms-vs-status-row">
                    <div class="hms-vs-status-name">{{ statusLabel(r.status) }}</div>
                    <div class="hms-vs-status-bars">
                      <div class="hms-vs-bar-wrap" :title="r.visits + ' visits'">
                        <div class="hms-vs-bar hms-vs-bar-visits" :style="{ width: barPct(r.visits, statusMaxVisits) + '%' }">
                          <span>{{ formatNum(r.visits) }}</span>
                        </div>
                      </div>
                      <div class="hms-vs-bar-wrap" :title="'KES ' + r.invoiced + ' invoiced'">
                        <div class="hms-vs-bar hms-vs-bar-money" :style="{ width: barPct(r.invoiced, statusMaxMoney) + '%' }">
                          <span>{{ formatMoneyShort(r.invoiced) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="hms-vs-legend mt-2">
                  <span><span class="hms-vs-swatch swatch-visits"></span> Visits</span>
                  <span><span class="hms-vs-swatch swatch-money"></span> Invoiced</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ROW 2: Timeline chart + Service Point breakdown -->
        <div class="row g-3">
          <div class="col-lg-7">
            <div class="hms-card">
              <div class="hms-card-head d-flex justify-content-between align-items-center">
                <span>Visits and Invoices Over Time</span>
                <div class="hms-tabs hms-tabs-sm">
                  <button class="hms-tab" :class="{active: timeSeriesMode==='day'}" @click="timeSeriesMode='day'">Day</button>
                  <button class="hms-tab" :class="{active: timeSeriesMode==='week'}" @click="timeSeriesMode='week'">Week</button>
                </div>
              </div>
              <div class="hms-card-body">
                <div v-if="!timelineSeries.length" class="hms-muted text-center hms-pad">No data.</div>
                <svg v-else class="hms-vs-line-svg" viewBox="0 0 800 240" preserveAspectRatio="none">
                  <!-- axis lines -->
                  <line x1="0"   y1="220" x2="800" y2="220" stroke="#cbd5e0" stroke-width="1"/>
                  <line x1="0"   y1="150" x2="800" y2="150" stroke="#edf2f7" stroke-dasharray="3 3"/>
                  <line x1="0"   y1="80"  x2="800" y2="80"  stroke="#edf2f7" stroke-dasharray="3 3"/>
                  <!-- visits line -->
                  <polyline :points="visitsPoints" fill="none" stroke="#7c3aed" stroke-width="2.5"/>
                  <!-- invoiced line -->
                  <polyline :points="invoicedPoints" fill="none" stroke="#16a34a" stroke-width="2.5"/>
                  <!-- x-axis labels: first, mid, last -->
                  <text v-for="(pt,i) in xLabels" :key="i" :x="pt.x" y="238" text-anchor="middle" fill="#718096" font-size="11">{{ pt.label }}</text>
                </svg>
                <div class="hms-vs-legend mt-2">
                  <span><span class="hms-vs-swatch" style="background:#7c3aed"></span> Visits</span>
                  <span><span class="hms-vs-swatch" style="background:#16a34a"></span> Invoiced Amount</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Visit Breakdown by Service Point -->
          <div class="col-lg-5">
            <div class="hms-card">
              <div class="hms-card-head">Visit Breakdown by Service Point</div>
              <div class="hms-card-body p-0">
                <table class="hms-table" v-if="servicePointRows.length">
                  <thead>
                    <tr>
                      <th>Queue Type</th>
                      <th class="text-end">Encounters <i class="mdi mdi-swap-vertical hms-muted-sm"></i></th>
                      <th class="text-end">Invoices Raised <i class="mdi mdi-swap-vertical hms-muted-sm"></i></th>
                      <th class="text-end">Total Originated</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="r in servicePointRows" :key="r.queue_type">
                      <td>{{ r.queue_type || '—' }}</td>
                      <td class="text-end">{{ formatNum(r.encounters) }}</td>
                      <td class="text-end">{{ formatNum(r.invoices_raised) }}</td>
                      <td class="text-end fw-bold">KES {{ formatNum(r.total_originated) }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-else class="hms-muted text-center hms-pad">No service-point activity in this period.</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.hms-page-body { padding: 16px 0 32px; }
.hms-back { color: #4a5568; font-size: 20px; text-decoration: none; }
.hms-page-title { font-size: 18px; font-weight: 600; color: #2d3748; }
.hms-filter { max-width: 220px; }
.hms-spinner { display: flex; justify-content: center; padding: 60px 0; }

/* Period cards */
.hms-vs-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px 16px; height: 100%; }
.hms-vs-lbl { color: #16a34a; font-weight: 700; font-size: 13px; text-align: center; margin-bottom: 4px; }
.hms-vs-sub { color: #a0aec0; font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px; text-align: center; margin-top: 6px; }
.hms-vs-num { color: #1a202c; font-weight: 700; font-size: 28px; text-align: center; }
.hms-vs-num-sm { color: #1a202c; font-weight: 700; font-size: 16px; text-align: center; }
.hms-vs-delta { font-size: 11px; margin-top: 2px; text-align: center; }
.hms-vs-delta.up   { color: #16a34a; }
.hms-vs-delta.down { color: #dc2626; }
.hms-vs-delta.flat { color: #718096; }
.hms-vs-sep { margin: 8px 0; border: 0; border-top: 1px dashed #e2e8f0; }

.hms-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; }
.hms-card-head { padding: 10px 14px; border-bottom: 1px solid #e2e8f0; font-weight: 700; color: #2d3748; font-size: 14px; }
.hms-card-body { padding: 14px; }
.hms-card-body.p-0 { padding: 0; }

/* Status bar chart */
.hms-vs-status-list { display: flex; flex-direction: column; gap: 8px; }
.hms-vs-status-row { display: grid; grid-template-columns: 140px 1fr; gap: 10px; align-items: center; }
.hms-vs-status-name { color: #4a5568; font-size: 13px; font-weight: 600; }
.hms-vs-status-bars { display: flex; flex-direction: column; gap: 4px; }
.hms-vs-bar-wrap { background: #f7fafc; border-radius: 4px; height: 22px; overflow: hidden; }
.hms-vs-bar { height: 100%; display: flex; align-items: center; padding: 0 8px; color: #fff; font-size: 11px; font-weight: 700; min-width: 40px; transition: width .3s ease; }
.hms-vs-bar-visits { background: #7c3aed; }
.hms-vs-bar-money  { background: #16a34a; }
.hms-vs-legend { display: flex; gap: 16px; font-size: 12px; color: #4a5568; }
.hms-vs-swatch { display: inline-block; width: 12px; height: 12px; border-radius: 2px; margin-right: 4px; vertical-align: middle; }
.swatch-visits { background: #7c3aed; }
.swatch-money  { background: #16a34a; }

/* Line chart */
.hms-vs-line-svg { width: 100%; height: 240px; }

.hms-table { width: 100%; }
.hms-table th, .hms-table td { padding: 8px 12px; border-bottom: 1px solid #e2e8f0; font-size: 13px; }
.hms-table th { background: #f7fafc; color: #4a5568; text-align: left; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.3px; }
.text-end { text-align: right; }
.fw-bold { font-weight: 700; }

.hms-input { padding: 6px 10px; border: 1px solid #cbd5e0; border-radius: 6px; font-size: 13px; }
.hms-btn { display: inline-flex; align-items: center; gap: 4px; padding: 6px 12px; border-radius: 6px; border: 1px solid transparent; font-size: 13px; font-weight: 500; cursor: pointer; }
.hms-btn-blue { background: #2b6cb0; color: #fff; }

.hms-muted { color: #a0aec0; }
.hms-muted-sm { color: #a0aec0; font-size: 11px; }
.hms-pad { padding: 24px; }

.hms-tabs-sm { display: inline-flex; gap: 4px; }
.hms-tab { padding: 4px 10px; font-size: 11px; border-radius: 6px; background: #f7fafc; color: #718096; border: 1px solid #e2e8f0; cursor: pointer; font-weight: 600; }
.hms-tab.active { background: #2b6cb0; color: #fff; border-color: #2b6cb0; }

@media print {
  .hms-page-header .hms-btn, .hms-back { display: none !important; }
  .hms-card, .hms-vs-card { break-inside: avoid; }
}
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "VisitsSummary",
  components: { Navigation },
  data() {
    return {
      loading: true,
      branchFilter: "",
      visitStatusFilter: "",
      paymentStatusFilter: "",
      branches: [],
      data: {},
      timeSeriesMode: 'day'
    };
  },
  computed: {
    periodCards() {
      var t = this.data.Today || {};
      var w = this.data.Week  || {};
      var m = this.data.Month || {};
      return [
        Object.assign({ key: 'today' }, t),
        Object.assign({ key: 'week'  }, w),
        Object.assign({ key: 'month' }, m)
      ];
    },
    byStatusRows() {
      return this.data.ByStatus || [];
    },
    statusMaxVisits() {
      return (this.byStatusRows.reduce(function(m, r) { return Math.max(m, r.visits); }, 0)) || 1;
    },
    statusMaxMoney() {
      return (this.byStatusRows.reduce(function(m, r) { return Math.max(m, r.invoiced); }, 0)) || 1;
    },
    timelineSeries() {
      var raw = this.data.Timeline || [];
      if (!raw.length) return [];
      if (this.timeSeriesMode === 'day') return raw;
      // Aggregate into ISO weeks
      var byWeek = {};
      raw.forEach(function(pt) {
        var d = new Date(pt.date);
        // ISO week key: YYYY-Www
        var yr = d.getFullYear();
        var oneJan = new Date(yr, 0, 1);
        var wk = Math.ceil((((d - oneJan) / 86400000) + oneJan.getDay() + 1) / 7);
        var key = yr + '-W' + String(wk).padStart(2, '0');
        if (!byWeek[key]) byWeek[key] = { date: key, visits: 0, invoiced: 0 };
        byWeek[key].visits   += pt.visits;
        byWeek[key].invoiced += pt.invoiced;
      });
      return Object.values(byWeek);
    },
    tlMax() {
      var s = this.timelineSeries;
      var maxV = s.reduce(function(m, p) { return Math.max(m, p.visits); }, 0) || 1;
      var maxI = s.reduce(function(m, p) { return Math.max(m, p.invoiced); }, 0) || 1;
      return { visits: maxV, invoiced: maxI };
    },
    visitsPoints() {
      var s = this.timelineSeries;
      if (s.length < 1) return '';
      var w = 800, h = 220, pad = 20;
      var xStep = s.length > 1 ? (w - 2*pad) / (s.length - 1) : 0;
      var self = this;
      return s.map(function(pt, i) {
        var x = pad + i * xStep;
        var y = h - pad - ((pt.visits / self.tlMax.visits) * (h - 2*pad));
        return x.toFixed(0) + ',' + y.toFixed(0);
      }).join(' ');
    },
    invoicedPoints() {
      var s = this.timelineSeries;
      if (s.length < 1) return '';
      var w = 800, h = 220, pad = 20;
      var xStep = s.length > 1 ? (w - 2*pad) / (s.length - 1) : 0;
      var self = this;
      return s.map(function(pt, i) {
        var x = pad + i * xStep;
        var y = h - pad - ((pt.invoiced / self.tlMax.invoiced) * (h - 2*pad));
        return x.toFixed(0) + ',' + y.toFixed(0);
      }).join(' ');
    },
    xLabels() {
      var s = this.timelineSeries;
      if (!s.length) return [];
      var w = 800, pad = 20;
      var pick = s.length <= 3 ? s.map(function(pt, i){ return {i:i, date:pt.date}; })
                              : [{i:0,date:s[0].date}, {i:Math.floor(s.length/2),date:s[Math.floor(s.length/2)].date}, {i:s.length-1,date:s[s.length-1].date}];
      var xStep = s.length > 1 ? (w - 2*pad) / (s.length - 1) : 0;
      return pick.map(function(p) {
        var lbl = p.date;
        if (/^\d{4}-\d{2}-\d{2}$/.test(lbl)) {
          var d = new Date(lbl);
          lbl = (d.getMonth()+1) + '/' + d.getDate();
        }
        return { x: pad + p.i * xStep, label: lbl };
      });
    },
    servicePointRows() {
      return this.data.ByServicePoint || [];
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.loadBranches();
    this.loadAll();
  },
  methods: {
    async loadBranches() {
      try {
        var r = await executeJson({ function: "getBranches" }, hms);
        if (r.data && r.data.success) this.branches = (r.data.data && r.data.data.Branches) || [];
      } catch(e) {}
    },
    async loadAll() {
      this.loading = true;
      try {
        var payload = { function: "getVisitsSummary" };
        if (this.branchFilter)        payload.branch_id      = this.branchFilter;
        if (this.visitStatusFilter)   payload.visit_status   = this.visitStatusFilter;
        if (this.paymentStatusFilter) payload.payment_status = this.paymentStatusFilter;
        var r = await executeJson(payload, hms);
        if (r.data && r.data.success) this.data = r.data.data || {};
      } catch(e) { console.error(e); }
      this.loading = false;
    },
    printPage() { window.print(); },
    formatNum(n) { if (n == null || n === '') return '0'; return Number(n).toLocaleString(); },
    formatMoneyShort(n) {
      var v = Number(n) || 0;
      if (v >= 1e6) return (v/1e6).toFixed(1) + 'M';
      if (v >= 1e3) return (v/1e3).toFixed(1) + 'K';
      return String(Math.round(v));
    },
    formatDelta(n) {
      if (n == null) return '0';
      return (n > 0 ? '+' : '') + Number(n).toLocaleString();
    },
    deltaClass(n) { if (!n || Math.abs(n) < 0.01) return 'flat'; return n > 0 ? 'up' : 'down'; },
    deltaIcon(n)  { if (!n || Math.abs(n) < 0.01) return 'mdi-minus'; return n > 0 ? 'mdi-arrow-up-thin' : 'mdi-arrow-down-thin'; },
    barPct(v, max) { if (!max) return 0; return Math.max(2, (v / max) * 100); },
    statusLabel(s) {
      var map = { waiting: 'Waiting', in_progress: 'In progress', completed: 'Finished', cancelled: 'Cancelled', no_show: 'No show' };
      return map[s] || (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g,' ') : '—');
    }
  }
};
</script>
